import React, { useCallback, useEffect, useMemo, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useHistory } from "react-router-dom"
import {
	deleteEntretien,
	deleteEntretienReset,
	fetchEntretiens,
	fetchEntretiensSemaine,
	postEntToAtelier,
	postEntToAtelierReset,
	postEntretienReset,
} from "../../store/actions/entretienActions"
import { fetchAtelierModeles } from "../../store/actions/atelierModeleActions"
import { useGroups } from "../../hooks/groups"
import { COLORS } from "../../utilities/Constants"
import { generalStyles as gs } from "../styles/styles"
import CustomModal from "../basics/interface/CustomModal"
import Loader from "../basics/Loader"
import EntretienAddEditModal from "./EntretienAddEditModal"
import EntretienCalendrier from "./EntretienCalendrier"
import EntretienDecalerModal from "./EntretienDecalerModal"
import EntretienEcheances from "./EntretienEcheances"
import EntretienHistoriqueDrawer from "./EntretienHistoriqueDrawer"
import EntretienPlans from "./EntretienPlans"
import { STATUTS, STATUT_KEYS, dotStyle } from "./entretienUtils"

const styles = {
	// Conteneur centré, comme ClientsTableV2 (le fond crème vient de gs.page).
	content: { width: "100%", maxWidth: 1180, margin: "0 auto" },
	header: {
		display: "flex",
		alignItems: "center",
		gap: 12,
		marginBottom: 20,
		flexWrap: "wrap",
	},
	// Compteurs = filtres de statut ; auto-fit → empilés en étroit.
	kpis: {
		display: "grid",
		gridTemplateColumns: "repeat(auto-fit, minmax(180px, 1fr))",
		gap: 12,
		marginBottom: 20,
	},
	kpi: (active, hover) => ({
		...gs.statCard,
		textAlign: "left",
		fontFamily: "inherit",
		cursor: "pointer",
		border: `1px solid ${
			active ? COLORS.text : hover ? COLORS.borderStrong : COLORS.border
		}`,
		boxShadow: active ? `inset 0 0 0 1px ${COLORS.text}` : "none",
	}),
	kpiLabel: {
		...gs.statLabel,
		display: "flex",
		alignItems: "center",
		gap: 6,
	},
	count: { fontSize: 11, color: COLORS.textLight, marginLeft: 6 },
	confirmTxt: { fontSize: 14, color: COLORS.textMuted },
}

const Kpi = ({ statut, n, active, onClick }) => {
	const [hover, setHover] = useState(false)
	const st = STATUTS[statut]
	return (
		<button
			type="button"
			style={styles.kpi(active, hover)}
			onMouseEnter={() => setHover(true)}
			onMouseLeave={() => setHover(false)}
			onClick={onClick}
		>
			<span style={{ ...gs.statValue, color: st.color }}>{n}</span>
			<span style={styles.kpiLabel}>
				<span style={dotStyle(st.color)} />
				{st.label}
			</span>
		</button>
	)
}

// Objet attendu par EntretienAddEditModal : champs d'édition du plan + échéance
// de préremplissage + véhicule de contexte (seul son cycle reprend la date, B3).
const entPourEdition = (plan, echeance) => ({
	...plan.edition,
	prochain_entretien: echeance
		? { date: echeance.date, km_cible: echeance.km_cible }
		: plan.prochain,
	vehicule_contexte: echeance ? echeance.vehicule.id : 0,
	vehicule_contexte_label: echeance ? echeance.vehicule.label : "",
})

const EntretienV2 = () => {
	const dispatch = useDispatch()
	const history = useHistory()
	const { isBoss } = useGroups()
	const {
		echeances,
		plans,
		createdAtelier,
		createAtelier,
		fetchEntretiens: fetchState,
		deleteEntretien: deleteState,
	} = useSelector((state) => state.entretienInfo)

	const [vue, setVue] = useState("echeances")
	const [calendrier, setCalendrier] = useState(false)
	const [statutFiltre, setStatutFiltre] = useState(null)
	const [drawerCycleId, setDrawerCycleId] = useState(null)
	const [decaler, setDecaler] = useState(null)
	// null = fermé ; { ent: null } = création ; { ent } = édition.
	const [edit, setEdit] = useState(null)
	const [deletePlan, setDeletePlan] = useState(null)
	const [newAtelierCycle, setNewAtelierCycle] = useState(null)

	useEffect(() => {
		dispatch(fetchEntretiens())
		dispatch(fetchAtelierModeles())
	}, [dispatch])

	useEffect(() => {
		if (createdAtelier > 0) {
			const atelierId = createdAtelier
			dispatch(postEntToAtelierReset())
			history.push(`/atelier/fiche/${atelierId}`)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [createdAtelier])

	const compteurs = useMemo(() => {
		const c = { retard: 0, du: 0, a_finaliser: 0, a_venir: 0 }
		echeances.forEach((e) => c[e.statut] !== undefined && c[e.statut]++)
		return c
	}, [echeances])
	const plansParId = useMemo(() => {
		const m = {}
		plans.forEach((p) => (m[p.id] = p))
		return m
	}, [plans])
	// Le tiroir suit la ligne à jour (après décalage / édition) ; fermé si
	// l'échéance a disparu.
	const drawerEcheance = useMemo(
		() => echeances.find((e) => e.cycle_id === drawerCycleId) || null,
		[echeances, drawerCycleId],
	)

	const voirFiche = (id) => history.push(`/atelier/fiche/${id}`)
	const creerFiche = (e) => setNewAtelierCycle(e.cycle_id)
	const closeNewAtelier = () => {
		setNewAtelierCycle(null)
		dispatch(postEntToAtelierReset())
	}
	// Calendrier : { atelier: id|null, id: cycleId }.
	const voirFicheAtelier = (cycle) => {
		if (!cycle) return
		if (cycle.atelier) voirFiche(cycle.atelier)
		else setNewAtelierCycle(cycle.id)
	}
	const editerPlan = (plan, echeance = null) =>
		plan && setEdit({ ent: entPourEdition(plan, echeance) })
	const closeEdit = () => {
		setEdit(null)
		dispatch(postEntretienReset())
	}
	const closeDelete = () => {
		setDeletePlan(null)
		dispatch(deleteEntretienReset())
	}
	const confirmDelete = () =>
		dispatch(deleteEntretien({ id: deletePlan.id })).then(() => {
			closeDelete()
			dispatch(fetchEntretiensSemaine({ silent: true }))
		})
	const closeDrawer = useCallback(() => setDrawerCycleId(null), [])

	const menuEcheance = (e) => [
		...(e.fiche
			? [{ label: "Voir la fiche", onClick: () => voirFiche(e.fiche.id) }]
			: []),
		{ label: "Décaler l'échéance", onClick: () => setDecaler(e) },
		{
			label: "Modifier le plan",
			onClick: () => editerPlan(plansParId[e.entretien.id], e),
		},
		...(isBoss
			? [
					{
						label: "Supprimer le plan",
						danger: true,
						onClick: () =>
							setDeletePlan(plansParId[e.entretien.id]),
					},
				]
			: []),
	]
	const menuPlan = (p) =>
		isBoss
			? [
					{
						label: "Supprimer",
						danger: true,
						onClick: () => setDeletePlan(p),
					},
				]
			: []

	const chargementInitial =
		fetchState.loading && echeances.length === 0 && plans.length === 0

	return (
		<div style={gs.page}>
			<div style={styles.content}>
				{newAtelierCycle && (
					<CustomModal
						title={"Créer une fiche d'atelier ?"}
						onClose={
							createAtelier.creating ? () => {} : closeNewAtelier
						}
						showCloseButton={!createAtelier.creating}
						buttons={[
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: closeNewAtelier,
								disabled: createAtelier.creating,
							},
							{
								title: "Créer la fiche",
								styleName: "primary",
								onClick: () =>
									dispatch(
										postEntToAtelier({
											id: newAtelierCycle,
										}),
									),
								loading: createAtelier.creating,
							},
						]}
					>
						<div style={styles.confirmTxt}>
							Une fiche d'atelier sera créée à partir de cette
							échéance (lignes du modèle copiées), puis ouverte.
						</div>
					</CustomModal>
				)}
				{edit && (
					<EntretienAddEditModal
						closeModal={closeEdit}
						ent={edit.ent}
					/>
				)}
				{decaler && (
					<EntretienDecalerModal
						echeance={decaler}
						onClose={() => setDecaler(null)}
					/>
				)}
				{deletePlan && (
					<CustomModal
						title={"Supprimer l'entretien ?"}
						onClose={deleteState.deleting ? () => {} : closeDelete}
						showCloseButton={!deleteState.deleting}
						buttons={[
							{
								title: "Annuler",
								styleName: "secondary",
								onClick: closeDelete,
								disabled: deleteState.deleting,
							},
							{
								title: "Supprimer",
								styleName: "danger",
								onClick: confirmDelete,
								loading: deleteState.deleting,
							},
						]}
					>
						<div style={styles.confirmTxt}>
							Le plan « {deletePlan.label} » et toutes ses
							échéances ({deletePlan.nb_vehicules} véhicule
							{deletePlan.nb_vehicules > 1 ? "s" : ""}) seront
							supprimés.
						</div>
					</CustomModal>
				)}
				{drawerEcheance && (
					<EntretienHistoriqueDrawer
						echeance={drawerEcheance}
						onClose={closeDrawer}
						onVoirFiche={voirFiche}
						onCreerFiche={creerFiche}
						onDecaler={setDecaler}
						onEditPlan={(e) =>
							editerPlan(plansParId[e.entretien.id], e)
						}
					/>
				)}

				<div style={styles.header}>
					<h2 style={{ ...gs.title, margin: 0, flex: 1 }}>
						Entretiens
					</h2>
					<button
						type="button"
						style={gs.btnSecondary}
						onClick={() => setCalendrier((c) => !c)}
					>
						{calendrier ? "Retour à la liste" : "Calendrier"}
					</button>
					<button
						type="button"
						style={gs.btnPrimary}
						onClick={() => setEdit({ ent: null })}
					>
						+ Nouvel entretien
					</button>
				</div>

				{calendrier ? (
					<EntretienCalendrier voirFicheAtelier={voirFicheAtelier} />
				) : chargementInitial ? (
					<Loader
						variant="ring"
						message="Chargement des entretiens…"
					/>
				) : (
					<>
						<div style={styles.kpis}>
							{STATUT_KEYS.map((k) => (
								<Kpi
									key={k}
									statut={k}
									n={compteurs[k]}
									active={statutFiltre === k}
									onClick={() => {
										setStatutFiltre((s) =>
											s === k ? null : k,
										)
										setVue("echeances")
									}}
								/>
							))}
						</div>

						<div style={gs.segmented}>
							<button
								type="button"
								style={gs.segment(vue === "echeances")}
								onClick={() => setVue("echeances")}
							>
								Échéances
								<span style={styles.count}>
									{echeances.length}
								</span>
							</button>
							<button
								type="button"
								style={gs.segment(vue === "plans")}
								onClick={() => setVue("plans")}
							>
								Plans d'entretien
								<span style={styles.count}>{plans.length}</span>
							</button>
						</div>

						{vue === "echeances" ? (
							<EntretienEcheances
								echeances={echeances}
								statutFiltre={statutFiltre}
								onOpen={(e) => setDrawerCycleId(e.cycle_id)}
								onCreerFiche={creerFiche}
								onVoirFiche={voirFiche}
								menuItems={menuEcheance}
							/>
						) : (
							<EntretienPlans
								plans={plans}
								onEditPlan={(p) => editerPlan(p)}
								menuItems={menuPlan}
							/>
						)}
					</>
				)}
			</div>
		</div>
	)
}

export default EntretienV2
